import React, { useRef, useEffect, useState } from "react";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { ScheduleHistoryListDatatable } from "@/helpers/scheduleHistoryListDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import TagList from "@/components/FormFields/TagList";
import DeviceStatus from "@/components/FormFields/DeviceStatus";
import DateRangePicker from "@/components/DateRangePicker";
import { deleteScheduledDeviceHistoryApi } from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import { IconFile } from "@/components/Icon";
import { useGlobalState } from "@/GlobalProvider";
import ScheduleTemplates from "@/components/FormFields/ScheduleTemplates";
import ScheduleTasks from "@/components/FormFields/ScheduleTasks";
import ExcuteStatus from "@/components/FormFields/ExcuteStatus";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import HasPermission from "@/components/commons/HasPermission";
import { useSearchParams } from "@/helpers/useSearchParams";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const ScheduleHistoryList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const shouldReset = useRef(false);

  const { scheduleTemplateId, taskType, executedStatus, startDate, endDate } =
    useSearchParams();

  const initialValues = {
    group: undefined,
    tag: undefined,
    template: scheduleTemplateId,
    tasks: taskType,
    deviceStatus: undefined,
    executedStatus: executedStatus,
    date: [
      Number(startDate) || dayjs().subtract(7, "d").startOf("d").valueOf(),
      Number(endDate) || dayjs().endOf("d").valueOf()
    ],
    keywords: ""
  };

  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;

    const getUTCDate = d =>
      dayjs(d)
        .add(new Date().getTimezoneOffset() * -1, "minutes")
        .valueOf();

    return {
      groupIds: formValues.group,
      tagIds: formValues.tag,
      scheduleTemplateId: formValues.template,
      taskType: formValues.tasks,
      deviceStatus: formValues.deviceStatus,
      executedStatus: formValues.executedStatus,
      executedDateStart: date[0] ? getUTCDate(date[0]) : undefined,
      executedDateEnd: date[1] ? getUTCDate(date[1]) : undefined,
      keywords: formValues.keywords
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = forceReset => {
    // prettier-ignore
    if (shouldReset.current) {
      initialValues.template = !forceReset ? initialValues.template : undefined;
      initialValues.tasks = !forceReset ? initialValues.tasks : undefined;
      initialValues.date = !forceReset ? initialValues.date : [null, null];
      initialValues.executedStatus = !forceReset ? initialValues.executedStatus : undefined;
      resetForm({
        values: initialValues
      });
    }
    shouldReset.current = true;
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedIds = tableRef.current.getSelected();

    const data = tableRef.current?.api().data().toArray();
    const selectedRows = data.filter(item => selectedIds.includes(item.id));

    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }
    toast.info(t({ defaultMessage: "未选择任何记录" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const doDelete = async rows => {
    try {
      setLoading(true);
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await deleteScheduledDeviceHistoryApi({
        scheduledDeviceHistoryIds: rows.map(row => row.id)
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除排程执行记录" }),
      content: t(
        {
          defaultMessage: "是否确认将（{name}）的排程执行记录删除？"
        },
        { name: row.deviceSerialNumber }
      ),
      onConfirm: () => {
        return doDelete(row);
      }
    });
  };

  const handleBatchDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除排程执行记录" }),
        content: t({
          defaultMessage: "是否确认将排程执行记录删除？"
        }),
        onConfirm: () => {
          return doDelete(selectedRowsRef.current);
        }
      });
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "排程执行历史记录" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="span"
                  className={classes.subTitle}
                >
                  {t({ defaultMessage: "仅显示近7天的历史纪录" })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "门店" })}
                  className={classes.formItem}
                >
                  <DeviceGroups
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "标签" })}
                  className={classes.formItem}
                >
                  <TagList
                    name="tag"
                    value={values.tag}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "排程模板" })}
                  className={classes.formItem}
                >
                  <ScheduleTemplates
                    name="template"
                    value={values.template}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "排程任务" })}
                  className={classes.formItem}
                >
                  <ScheduleTasks
                    name="tasks"
                    value={values.tasks}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "设备状态" })}
                    className={classes.formItem}
                  >
                    <DeviceStatus
                      name="deviceStatus"
                      value={values.deviceStatus}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "执行状态" })}
                    className={classes.formItem}
                  >
                    <ExcuteStatus
                      name="executedStatus"
                      value={values.executedStatus}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "实际执行时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={() => resetQuery(true)}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <ScheduleHistoryListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onDelete={handleDelete}
          onLoaded={handleLoaded}
          footer={
            <HasPermission permission="schedule_task_history_alarm_delete">
              <SubmitButton
                variant="outlined"
                color="primary"
                loading={loading}
                onClick={handleBatchDelete}
              >
                {t({ defaultMessage: "批量删除" })}
              </SubmitButton>
            </HasPermission>
          }
        />
      </Paper>
    </div>
  );
};

export default ScheduleHistoryList;
